import { ServiceProviderRowComponent } from './components/service-provider-row/service-provider-row.component';
import { BrPopoverTriggerDirective } from './shared/ui/popover/popover-trigger.directive';
import { DataListLoadMoreComponent } from './shared/ui/data-list/data-list-load-more.component';
import { CampaignProgressChartsComponent } from './pages/leads/campaigns/campaign-progress-charts.component';
import { CampaignGoalChartsComponent } from './pages/leads/campaigns/campaign-goal-charts.component';
import { CampaignTargetsComponent } from './pages/leads/campaigns/campaign-targets.component';
import { CampaignsWorkspaceComponent } from './pages/leads/campaigns/campaigns-workspace.component';
import { CampaignDetailComponent } from './pages/leads/campaigns/campaign-detail.component';
import { CampaignsComponent } from './pages/leads/campaigns/campaigns.component';
import { ReportTemplatesWorkspaceComponent } from './pages/home/report-templates/report-templates-workspace.component';
import { ReportTemplatesComponent } from './pages/home/report-templates/report-templates.component';
import { ReportTemplateDetailComponent } from './pages/home/report-templates/report-template-detail.component';
import { SelectionSummaryComponent } from './map/components/selection-summary/selection-summary.component';
import { ComposerTooltipDirective } from './components/command-bar/composer-tooltip.directive';
import { DataListCardComponent } from './shared/ui/data-list/data-list-card.component';
import { DataListComponent } from './shared/ui/data-list/data-list.component';
import { LocationPreviewComponent } from './map/components/location-preview/location-preview.component';
import { MapPinComponent } from './map/components/map-pin/map-pin.component';
import { ShowOnMapDirective } from './map/components/map-pin/show-on-map.directive';
import { DataListItemDirective, DataListDetailDirective } from './shared/ui/data-list/data-list-item.directive';
import { WorkspaceViewDirective } from './components/workspace-view/workspace-view.directive';
import { ProspectLegendComponent } from './map/features/prospects/prospect-legend.component';
import { DensityLegendComponent } from './map/features/density/density-legend.component';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { APP_INITIALIZER, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgChartsModule } from 'ng2-charts';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ClipboardModule } from '@angular/cdk/clipboard';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { LayoutModule } from '@angular/cdk/layout';
import { CdkScrollableModule } from '@angular/cdk/scrolling';

// Angular Material
import { WorkspaceIconsModule } from './shared/workspace-icons.module';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialogModule } from '@angular/material/dialog';
import { MatTableModule } from '@angular/material/table';
import { MatSortModule } from '@angular/material/sort';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatChipsModule } from '@angular/material/chips';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatCardModule } from '@angular/material/card';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatListModule } from '@angular/material/list';
import { MatBadgeModule } from '@angular/material/badge';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTabsModule } from '@angular/material/tabs';
import { MatStepperModule } from '@angular/material/stepper';
import { MatRadioModule } from '@angular/material/radio';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { NgxFileDropModule } from 'ngx-file-drop';

import { BrIconComponent } from './shared/ui/icon/icon.component';
import { BrMenuComponent } from './shared/ui/menu/menu.component';
import { BrMenuItemComponent } from './shared/ui/menu/menu-item.component';
import { BrMenuTriggerDirective } from './shared/ui/menu/menu-trigger.directive';

import { AppRoutingModule } from './app-routing.module';
import { UiGalleryComponent } from './pages/ui-gallery/ui-gallery.component';
import { AppComponent } from './app.component';
import { AuthService } from './services/auth.service';
import { AuthInterceptor } from './interceptors/auth.interceptor';
import { UnauthorizedInterceptor } from './interceptors/unauthorized.interceptor';

// Configuration: identity and access
import { LoginDialogComponent } from './components/login-dialog/login-dialog.component';
import { ResetPasswordComponent } from './pages/auth/reset-password/reset-password.component';
import { ChangePasswordDialogComponent } from './configuration/account/change-password-dialog.component';
import { ConfigurationUsersComponent } from './configuration/users/users.component';
import { UserDetailComponent } from './configuration/users/user-detail.component';
import { UserCreateDialogComponent } from './configuration/users/user-create-dialog.component';
import { UserInviteDialogComponent } from './configuration/users/user-invite-dialog.component';
import { UserEditDialogComponent } from './configuration/users/user-edit-dialog.component';
import { UserRoleAssignDialogComponent } from './configuration/users/user-role-assign-dialog.component';
import { ConfigurationRolesComponent } from './configuration/roles/roles.component';
import { RoleEditComponent } from './configuration/roles/role-edit.component';
import { ApiKeyListComponent } from './configuration/apikeys/api-key-list.component';
import { ConfigurationApiKeysComponent } from './configuration/apikeys/apikeys.component';
import { VoiceSettingsComponent } from './configuration/voice-settings/voice-settings.component';
import { CliProvidersComponent } from './components/cli-providers/cli-providers.component';
import { ApiKeyEditorComponent } from './configuration/apikeys/api-key-editor.component';
import { ApiKeyCreateDialogComponent } from './configuration/apikeys/apikey-create-dialog.component';

// Configuration and business-owned catalogs
import { ConfigTableComponent } from './pages/configuration/catalogs/config-table.component';
import { FileTreeComponent } from './pages/files/file-tree.component';
import { FileThumbnailComponent } from './pages/files/file-thumbnail.component';
import { FilesComponent } from './pages/files/files.component';
import { FileDropTargetDirective } from './pages/files/file-drop-target.directive';
import { PendingContractsComponent } from './pages/crm/contracts/pending-contracts.component';
import { ConfigAddDialogComponent } from './pages/configuration/catalogs/config-add-dialog.component';
import { ConfigDeactivateDialogComponent } from './pages/configuration/catalogs/config-deactivate-dialog.component';
import { CustomerTypeConfigComponent } from './pages/configuration/catalogs/customer-type-config.component';
import { LeadStageConfigComponent } from './pages/configuration/catalogs/lead-stage-config.component';
import { BillingStatusConfigComponent } from './pages/configuration/catalogs/billing-status-config.component';
import { PricingAreaConfigComponent } from './pages/configuration/catalogs/pricing-area-config.component';
import { CommPrefConfigComponent } from './pages/configuration/catalogs/comm-pref-config.component';
import { TransactionCodeConfigComponent } from './pages/crm/transaction-codes/transaction-code-config.component';
import { MaterialTypeConfigComponent } from './pages/configuration/catalogs/material-type-config.component';
import { VehicleTypeConfigComponent } from './pages/configuration/catalogs/vehicle-type-config.component';
import { DepotConfigComponent } from './pages/configuration/catalogs/depot-config.component';
import { SelectionWorkspaceComponent } from './pages/map/selection/selection-workspace.component';
import { CustomersComponent } from './pages/crm/customers/customers.component';
import { CustomerWorkspaceComponent } from './pages/crm/customers/customer-workspace.component';
import { LeadsBoardComponent } from './pages/leads/board/leads-board.component';
import { LeadsWorkspaceComponent } from './pages/leads/board/leads-workspace.component';
import { LeadPageComponent } from './pages/leads/board/lead-page.component';
import { LeadProfileFieldsComponent } from './pages/leads/board/lead-profile-fields.component';
import { WorkItemDetailComponent } from './pages/leads/board/work-item-detail.component';
import { LeadQuotesComponent } from './pages/leads/board/lead-quotes.component';
import { RollOffBoardComponent } from './pages/leads/rolloff/rolloff-board.component';
import { RollOffWorkspaceComponent } from './pages/leads/rolloff/rolloff-workspace.component';
import { RollOffHireDialogComponent } from './pages/leads/rolloff/rolloff-hire-dialog.component';
import { RollOffHireDetailComponent } from './pages/leads/rolloff/rolloff-hire-detail.component';
import { LeadsViewSwitcherComponent } from './pages/leads/board/leads-view-switcher.component';
import { LeadsAgentStripComponent } from './leads/agent/leads-agent-strip.component';
import { AgentQueueComponent } from './leads/agent/agent-queue.component';
import { TerritoriesComponent } from './pages/leads/territory/territories.component';
import { SalesRouteComponent } from './pages/leads/sales-route/sales-route.component';
import { NurtureComponent } from './pages/leads/nurture/nurture.component';
import { CommandCenterComponent } from './pages/leads/command-center/command-center.component';
import { LeadsOverviewComponent } from './pages/leads/overview/leads-overview.component';
import { ApprovalsPanelComponent } from './pages/leads/approvals/approvals-panel.component';
import { BoardColumnComponent } from './components/board-column/board-column.component';
import { TableImportDialogComponent } from './components/table-import-dialog/table-import-dialog.component';
import { TerritoryPlanDialogComponent } from './pages/leads/territory/territory-plan-dialog.component';
import { TableExportDialogComponent } from './components/table-export-dialog/table-export-dialog.component';
import { ImportMappingStepComponent } from './components/table-import-dialog/import-mapping-step.component';
import { ImportPlanStepComponent } from './components/table-import-dialog/import-plan-step.component';
import { ImportResultStepComponent } from './components/table-import-dialog/import-result-step.component';
import { CustomerProfileEditorComponent } from './pages/crm/customers/customer-profile-editor.component';
import { CustomerPageComponent } from './pages/crm/customers/customer-page.component';
import { CustomerProfileFieldsComponent } from './pages/crm/customers/customer-profile-fields.component';
import { AddChildDialogComponent } from './pages/crm/customers/add-child-dialog.component';
import { CrmCasesComponent } from './pages/crm/cases/cases.component';
import { CrmCasesWorkspaceComponent } from './pages/crm/cases/cases-workspace.component';
import { CrmFollowUpsComponent } from './pages/crm/follow-ups/follow-ups.component';
import { ActivityTimelineComponent } from './crm/activity-timeline/activity-timeline.component';
import { SignaturePadComponent } from './shared/ui/signature-pad/signature-pad.component';
import { SignContractDialogComponent } from './pages/crm/customers/sign-contract-dialog.component';
import { SignPageComponent } from './pages/crm/contracts/sign/sign-page.component';
import { CreateContractDialogComponent } from './pages/crm/customers/create-contract-dialog.component';
import { ContractDocumentsDialogComponent } from './pages/crm/contracts/contract-documents-dialog.component';
import { DateFieldComponent } from './shared/ui/date-field/date-field.component';
import { TimeFieldComponent } from './shared/ui/time-field/time-field.component';
import { CrmCaseDetailComponent } from './pages/crm/cases/case-detail.component';
import { ContactDialogComponent } from './crm/contacts/contact-dialog.component';
import { CaseCreateDialogComponent } from './pages/crm/cases/case-create-dialog.component';
import { MapComponent } from './map/components/map/map.component';
import { DataFilterComponent } from './shared/ui/data-filter/data-filter.component';
import { MilestoneRailComponent } from './components/milestone-rail/milestone-rail.component';
import { UndoButtonComponent } from './components/undo-button/undo-button.component';
import { TableTransferActionsComponent } from './components/table-transfer-actions/table-transfer-actions.component';
import { DataTableComponent } from './shared/ui/data-table/data-table.component';
import { DataTableCellDirective } from './shared/ui/data-table/data-table-cell.directive';
import { EditSessionBarComponent } from './shared/edit-session/edit-session-bar.component';
import { WorkspaceRootComponent } from './components/workspace-root/workspace-root.component';
import { WorkspaceBreadcrumbsComponent } from './components/workspace/workspace-breadcrumbs.component';
import { WorkspaceComponent } from './components/workspace/workspace.component';
import { WorkspaceMasterDetailComponent } from './components/workspace-master-detail/workspace-master-detail.component';
import { ComposerModelOptionsComponent } from './components/command-bar/composer-model-options.component';
import { ConversationAgentsComponent } from './components/command-bar/conversation-agents.component';
import { PlaceEnrichmentTaskComponent } from './components/command-bar/place-enrichment-task.component';
import { ConversationTasksComponent } from './components/command-bar/conversation-tasks.component';
import { CommandBarComponent } from './components/command-bar/command-bar.component';
import { ChatMarkdownComponent } from './components/command-bar/chat-markdown.component';
import { NavbarComponent } from './components/navbar/navbar.component';
import { PullConfirmDialogComponent } from './components/pull-confirm-dialog/pull-confirm-dialog.component';
import { CliLoginDialogComponent } from './components/cli-login-dialog/cli-login-dialog.component';
import { SidebarComponent } from './components/sidebar/sidebar.component';
import { MapLayerMenuComponent } from './map/components/layer-menu/map-layer-menu.component';
import { LandingComponent } from './components/landing/landing.component';
import { CompanyInformationComponent } from './configuration/account/company-information.component';
import { ConfigurationDialogComponent } from './configuration/configuration-dialog/configuration-dialog.component';
import { SidebarControlComponent } from './components/sidebar-control/sidebar-control.component';
import { HomeViewComponent } from './pages/home/home-view.component';
import { DashboardViewComponent } from './pages/dashboard/dashboard-view.component';
import { ProspectControlComponent } from './components/sidebar-control/prospect-control/prospect-control.component';
import { PullActivityComponent } from './pages/map/prospect/pull-activity.component';
import { ProspectWorkspaceComponent } from './pages/map/prospect/prospect-workspace.component';
import { TerritoryControlComponent } from './components/sidebar-control/territory-control/territory-control.component';
import { SalesRouteControlComponent } from './components/sidebar-control/sales-route-control/sales-route-control.component';
import { ResizeXDirective } from './directives/resize-x.directive';
import { MapFeatureManagerService } from './map/core/map-feature-manager.service';
import { MapNavigationService } from './map/core/map-navigation.service';

// Phase 05-01
import { CompanyConfigurationComponent }    from './pages/configuration/company/company-configuration.component';
// Phase 06-01 Security / Audit Log
import { AuditLogComponent } from './pages/configuration/audit-log/audit-log.component';
import { JobSchedulesComponent } from './pages/configuration/job-schedules/job-schedules.component';
import { DedupeComponent } from './pages/dedupe/dedupe.component';
import { RollOffListComponent } from './pages/leads/rolloff/rolloff-list.component';

@NgModule({
  declarations: [
    UiGalleryComponent,
    CampaignsWorkspaceComponent, CampaignsComponent, CampaignDetailComponent,
    ReportTemplatesWorkspaceComponent, ReportTemplatesComponent, ReportTemplateDetailComponent,
    RollOffListComponent,
    DedupeComponent,
    AppComponent,
    MapComponent,
    CommandBarComponent,
    NavbarComponent,
    PullConfirmDialogComponent,
    CliLoginDialogComponent,
    SidebarComponent,
    MapLayerMenuComponent,
    LandingComponent,
    ConfigurationDialogComponent,
    WorkspaceRootComponent,
    WorkspaceComponent,
    WorkspaceMasterDetailComponent,
    SidebarControlComponent,
    HomeViewComponent,
    DashboardViewComponent,
    ProspectControlComponent,
    ProspectWorkspaceComponent,
    TerritoryControlComponent,
    SalesRouteControlComponent,
    ResizeXDirective,
    // Phase 01-01: Identity & Auth
    LoginDialogComponent,
    ResetPasswordComponent,
    ChangePasswordDialogComponent,
    ConfigurationUsersComponent,
    UserDetailComponent,
    UserCreateDialogComponent,
    UserInviteDialogComponent,
    UserEditDialogComponent,
    UserRoleAssignDialogComponent,
    ConfigurationRolesComponent,
    RoleEditComponent,
    ConfigurationApiKeysComponent,
    CliProvidersComponent,
    VoiceSettingsComponent,
    ApiKeyCreateDialogComponent,
    // Config
    ConfigTableComponent,
    ConfigAddDialogComponent,
    ConfigDeactivateDialogComponent,
    DepotConfigComponent,
    CustomerTypeConfigComponent,
    LeadStageConfigComponent,
    BillingStatusConfigComponent,
    PricingAreaConfigComponent,
    CommPrefConfigComponent,
    TransactionCodeConfigComponent,
    MaterialTypeConfigComponent,
    VehicleTypeConfigComponent,
    CrmCasesComponent,
    CrmCasesWorkspaceComponent,
    CrmFollowUpsComponent,
    ActivityTimelineComponent,
    SignaturePadComponent,
    SignContractDialogComponent,
    SignPageComponent,
    CreateContractDialogComponent,
    ContractDocumentsDialogComponent,
    DateFieldComponent,
    TimeFieldComponent,
    CrmCaseDetailComponent,
    ContactDialogComponent,
    CaseCreateDialogComponent,
    PendingContractsComponent,
    SelectionWorkspaceComponent,
    FilesComponent,
    CustomersComponent,
    CustomerWorkspaceComponent,
    LeadsBoardComponent,
    LeadsWorkspaceComponent,
    LeadPageComponent,
    CampaignTargetsComponent,
    LeadProfileFieldsComponent,
    WorkItemDetailComponent,
    LeadQuotesComponent,
    RollOffBoardComponent,
    RollOffWorkspaceComponent,
    RollOffHireDialogComponent,
    RollOffHireDetailComponent,
    LeadsAgentStripComponent,
    AgentQueueComponent,
    TerritoriesComponent,
    SalesRouteComponent,
    NurtureComponent,
    CommandCenterComponent,
    LeadsOverviewComponent,
    ApprovalsPanelComponent,
    BoardColumnComponent,
    TableImportDialogComponent,
    TerritoryPlanDialogComponent,
    TableExportDialogComponent,
    ImportMappingStepComponent,
    ImportPlanStepComponent,
    ImportResultStepComponent,
    CustomerProfileEditorComponent,
    CustomerPageComponent,
    CustomerProfileFieldsComponent,
    AddChildDialogComponent,
    // Phase 05-01
    CompanyConfigurationComponent,
    // Phase 06-01 Security / Audit Log
    AuditLogComponent,
    JobSchedulesComponent,
  ],
  imports: [
    CompanyInformationComponent,
    ServiceProviderRowComponent,
    BrIconComponent, BrMenuComponent, BrMenuItemComponent, BrMenuTriggerDirective, BrPopoverTriggerDirective,
    SelectionSummaryComponent,
    ApiKeyListComponent,
    ApiKeyEditorComponent,
    ChatMarkdownComponent, ComposerTooltipDirective, ComposerModelOptionsComponent, ConversationTasksComponent, ConversationAgentsComponent, PlaceEnrichmentTaskComponent,
    DensityLegendComponent, ProspectLegendComponent,
    DataFilterComponent,
    // The pipeline drawn as a track. Shared by the Leads collection and
    // Roll-off Hires; it used to be inline markup on the first of those.
    MilestoneRailComponent,
    // The lasting offer to take a move back, on both boards.
    UndoButtonComponent,
    PullActivityComponent,
    // Import and Find duplicates, for every table that has a transfer.
    TableTransferActionsComponent,
    WorkspaceBreadcrumbsComponent, FileTreeComponent, FileThumbnailComponent, FileDropTargetDirective,
    DataTableComponent, DataListLoadMoreComponent, DataListCardComponent, DataListComponent, DataListItemDirective, DataListDetailDirective, WorkspaceViewDirective, MapPinComponent, LocationPreviewComponent, ShowOnMapDirective,
    DataTableCellDirective,
    // Save / Cancel / Undo + the "This edit session" strip (Sales Route).
    EditSessionBarComponent,
    // List | Board | Analysis — spans Leads page modes and the analysis route.
    LeadsViewSwitcherComponent,
    BrowserModule,
    BrowserAnimationsModule,
    HttpClientModule,
    AppRoutingModule,
    FormsModule,
    NgChartsModule,
    ReactiveFormsModule,
    ClipboardModule,
    DragDropModule,
    LayoutModule,
    // Registers the scroll containers a drag is measured against; see the
    // `cdkScrollable` uses in workspace-master-detail and the Leads board.
    CdkScrollableModule,
    // Angular Material
    WorkspaceIconsModule,
    MatButtonModule,
    MatTooltipModule,
    MatDialogModule,
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatChipsModule,
    MatCheckboxModule,
    MatExpansionModule,
    MatSnackBarModule,
    MatProgressBarModule,
    CampaignProgressChartsComponent,
    CampaignGoalChartsComponent,
    MatProgressSpinnerModule,
    MatCardModule,
    MatToolbarModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatListModule,
    MatBadgeModule,
    MatSlideToggleModule,
    MatTabsModule,
    MatStepperModule,
    MatRadioModule,
    MatAutocompleteModule,
    MatButtonToggleModule,
    NgxFileDropModule,
  ],
  providers: [
    MapFeatureManagerService,
    MapNavigationService,
    // Rehydrate the user's permission set on startup (after a page refresh the
    // JWT survives in localStorage but the in-memory permission cache does not),
    // so permission-gated UI is correct on first render. No-op when logged out.
    {
      provide: APP_INITIALIZER,
      multi: true,
      deps: [AuthService],
      // Session expiry cancels the permission stream without a value. Angular
      // waits for Observable completion, so the signed-out shell can still start.
      useFactory: (auth: AuthService) => () => auth.loadPermissions(),
    },
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: UnauthorizedInterceptor, multi: true },
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }
